import { useEffect, useRef, useState } from 'react';

/**
 * "Type this word to confirm" — THE ONE IMPLEMENTATION of the deliberate gesture.
 *
 * It was born inline in `features/targets/TargetsScreen.tsx` as the arm switch that lets the
 * live-verification agent touch a real production system. V0.48 gave it a second caller: the
 * Backups tab, where confirming replaces every account, document and setting in the
 * installation with the contents of a file. Those are the two most consequential buttons in
 * the app and they must ask in exactly the same way — a reader who has learned the gesture
 * once should not have to work out whether this one behaves the same.
 *
 * LIFTED RATHER THAN COPIED, for the reason `features/permissions/fields.tsx` records at the
 * top of its own file: the alternative to lifting a shared piece is a second copy of it, which
 * is the failure this codebase has already paid for once. Withholding the lift is right while
 * a thing has ONE caller; this one now has two.
 *
 * THE MARKUP IS UNCHANGED FROM THE ARM SWITCH, class for class and element for element
 * (`.arm-confirm` > `.arm-warn`, `.fld`, `.arm-row` > two `.pbtn`), because
 * `e2e/26-targets.spec.ts` drives the gesture through those selectors and that spec is the
 * acceptance gate, not a detail. The class names keep their `arm-` prefix for the same reason:
 * renaming them would be a UI change nobody asked for, and the stylesheet is where the names
 * are real.
 *
 * EXACT MATCH, NEVER "close enough" — no trimming, no case folding. The whole point of the
 * gesture is that it cannot be performed by accident or by muscle memory, and a confirmation
 * that forgives a stray space is one a fast hand completes without reading. The same string is
 * sent to the server, which re-checks it; this button is the ceremony, not the enforcement.
 *
 * IT OWNS WHAT WAS TYPED, and the caller owns whether the gesture is on screen at all. So
 * cancelling — or succeeding — unmounts this and the field is empty next time, which is the
 * property `26-targets.spec.ts` calls "an abandoned confirmation cannot be completed by one
 * later click". A failed `onConfirm` leaves the text in place, so a server refusal can be read
 * and answered without typing the word again.
 */
export function TypedConfirm({
  prompt,
  expected,
  actionLabel,
  onConfirm,
  onCancel,
  cancelLabel = 'Cancel',
  busy = false,
}: {
  /** What is about to happen, and what to type. Rich text, because both callers bold the word. */
  prompt: React.ReactNode;
  /** The word that must be typed back, character for character. */
  expected: string;
  /** The verb on the confirming button — "Arm", "Restore". */
  actionLabel: string;
  onConfirm: (typed: string) => void | Promise<void>;
  onCancel: () => void;
  cancelLabel?: string;
  /** While the caller is acting on a confirmation, so it cannot be sent twice. */
  busy?: boolean;
}) {
  const [typed, setTyped] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // The gesture appears in place of a button somebody just clicked, so the caret belongs in the
  // field: asking someone to type a word and then making them find the box is a small rudeness
  // that a confirmation dialog cannot afford.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div className="arm-confirm">
      <div className="arm-warn">{prompt}</div>
      <input
        ref={inputRef}
        className="fld"
        placeholder={expected}
        value={typed}
        disabled={busy}
        onChange={(e) => setTyped(e.target.value)}
      />
      <div className="arm-row">
        <button className="pbtn primary" disabled={busy || typed !== expected} onClick={() => void onConfirm(typed)}>
          {actionLabel}
        </button>
        <button className="pbtn" disabled={busy} onClick={onCancel}>
          {cancelLabel}
        </button>
      </div>
    </div>
  );
}
